import { map } from 'rxjs/operators'
import { Component, OnInit } from '@angular/core'
import { Notification, NotificationLinkTypes } from '@tv3/store/notification/notification.model'
import { NotificationService } from '@tv3/store/notification/notification.service'
import { ActionFailed, DataCheckerService, Destroyable, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { NotificationDetailService } from '@tv3/services/notification-detail.service'
import { orderBy } from 'lodash'
import { MatDialogRef } from '@angular/material/dialog'
import { select, Store } from '@ngrx/store'
import { IncidentSeverity, IncidentsGuard, IncidentView, selectIncidents } from '@tokeet-frontend/incident'
import { combineLatest } from 'rxjs'
import { OpenEditIncidentDialog, OpenEditTaskDialog } from '@tv3/store/overlay/overlay.actions'
import { getTaskStatusColor, TaskView } from '@tokeet-frontend/tasks'
import { selectAllTasksViews } from '@tv3/store/task/task.selectors'
import { TaskGuard } from '@tokeet-frontend/tasks'
import { ReadAllNotificationsComplete } from '@tv3/store/notification/notification.actions'
import { selectNotificationUnreadCount } from '@tv3/store/notification/notification.selectors'

@Component({
  selector: 'app-system-notifications-overlay',
  templateUrl: './system-notifications-overlay.component.html',
  styleUrls: ['./system-notifications-overlay.component.scss'],
})
export class SystemNotificationsOverlayComponent extends Destroyable implements OnInit {
  allNotifications: Notification[] = []
  notifications: Notification[] = []
  incidents: IncidentView[] = []
  tasks: TaskView[] = []
  showCount = 10
  unreadCount = 0

  // Pagination properties
  pageSize = 10
  pageSizeOptions = [5, 10, 20, 50]
  currentPage = 0
  paginatedNotifications: Notification[] = []
  paginatedIncidents: IncidentView[] = []
  paginatedTasks: TaskView[] = []

  theme = {
    booked: {
      clickable: true,
      color: 'info',
      icon: 'cs-icon cs-icon-calendar-check2',
    },
    cancelled: {
      clickable: true,
      color: 'danger',
      icon: 'cs-icon cs-icon-calendar-times',
    },
    inquiry: {
      clickable: true,
      color: 'warning',
      icon: 'cs-icon cs-icon-calendar-pencil',
    },
    event: {
      clickable: true,
      color: 'warning',
      icon: 'cs-icon cs-icon-calendar-ban',
    },
    paid: {
      clickable: true,
      color: 'success',
      icon: 'cs-icon cs-icon-dollar-check',
    },
    signed: {
      clickable: true,
      color: 'indigo',
      icon: 'cs-icon cs-icon-pen-sign',
    },
    rejected: {
      clickable: true,
      color: 'danger',
      icon: 'cs-icon cs-icon-times',
    },
    succeeded: {
      clickable: true,
      color: 'success',
      icon: 'cs-icon cs-icon-dollar-check',
    },
    failed: {
      clickable: true,
      color: 'danger',
      icon: 'cs-icon cs-icon-alert',
    },
  }

  tabs = [
    {
      id: 'incident',
      amount: 0,
      title: 'Incident',
      emptyImage: 'assets/images/empty_icons/notification-incident.svg',
      emptyTitle: $localize`No Incidents Logged Yet`,
      emptyDescription: $localize`When issues arise—like maintenance problems or guest complaints—you’ll see them listed here for quick action.`,
    },
    {
      id: 'task',
      amount: 0,
      title: 'Task',
      emptyImage: 'assets/images/empty_icons/notification-incident.svg',
      emptyTitle: $localize`No Tasks Yet`,
      emptyDescription: $localize`When issues arise—like maintenance problems or guest complaints—you’ll see them listed here for quick action.`,
    },
    {
      id: 'inquiry',
      amount: 0,
      title: 'Inquiry',
      emptyImage: 'assets/images/empty_icons/notification-inquiry.svg',
      emptyTitle: $localize`No Inquiries Yet`,
      emptyDescription: $localize`Start receiving guest inquiries once your listings go live. We’ll notify you here as soon as someone reaches out.`,
    },
    {
      id: 'booked',
      amount: 0,
      title: 'Booking',
      emptyImage: 'assets/images/empty_icons/notification-booking.svg',
      emptyTitle: $localize`No Bookings Yet`,
      emptyDescription: $localize`Your confirmed bookings will appear here once guests finalize their stay. Keep your calendar ready!`,
    },
    {
      id: 'cancelled',
      amount: 0,
      title: 'Cancellation',
      emptyImage: 'assets/images/empty_icons/notification-cancel.svg',
      emptyTitle: $localize`No Cancellations Reported`,
      emptyDescription: $localize`Good news! There haven’t been any cancellations. We’ll update this section if any bookings are called off.`,
    },
    {
      id: 'event',
      amount: 0,
      title: 'Hold Event',
      emptyImage: 'assets/images/empty_icons/notification-cancel.svg',
      emptyTitle: $localize`No Hold Events Yet`,
      emptyDescription: $localize`Looks like there aren't any hold events scheduled. Create a new hold event and manage availability for your rentals.`,
    },
    {
      id: 'paid',
      amount: 0,
      title: 'Paid',
      emptyImage: 'assets/images/empty_icons/notification-paid.svg',
      emptyTitle: $localize`No Payments Yet`,
      emptyDescription: $localize`Once guests complete their payments, you’ll see them listed here. Stay tuned for your next payout!`,
    },
    {
      id: 'signed',
      amount: 0,
      title: 'Signed',
      emptyImage: 'assets/images/empty_icons/notification-signed.svg',
      emptyTitle: $localize`No Signed Contracts Yet`,
      emptyDescription: $localize`Once a guest signs the contract, you’ll get notified right here. Stay ready to move forward confidently!`,
    },
    {
      id: 'rejected',
      amount: 0,
      title: 'Rejection',
      emptyImage: 'assets/images/empty_icons/notification-rejected.svg',
      emptyTitle: $localize`No Contract Rejections Received`,
      emptyDescription: $localize`You're all clear — no contracts have been rejected. We’ll notify you here if that happens`,
    },
  ]

  activeTab

  constructor(
    private dialogRef: MatDialogRef<SystemNotificationsOverlayComponent>,
    private store: Store<any>,
    private notificationService: NotificationService,
    private notificationDetailService: NotificationDetailService,
    private dataChecker: DataCheckerService
  ) {
    super()
    this.dataChecker.check([IncidentsGuard, TaskGuard])
  }

  ngOnInit() {
    this.onReadAll()

    this.activeTab = this.tabs[0]
    combineLatest([
      this.notificationService.all().pipe(map((n2) => orderBy([...n2], ['date'], ['desc']))),
      this.store.select(selectIncidents),
      this.store.select(selectAllTasksViews()),
    ])
      .pipe(untilDestroy(this))
      .subscribe(([notifications, incidents, tasks]) => {
        this.incidents = orderBy(incidents, ['created'], ['desc'])
        this.tasks = orderBy(tasks, ['created'], ['desc'])
        this.allNotifications = notifications
        const stats = this.allNotifications.reduce((res, i) => ({ ...res, [i.type]: (res[i.type] || 0) + 1 }), {})
        this.tabs[0].amount = this.incidents.length
        this.tabs[1].amount = this.tasks.length
        this.tabs.slice(2).forEach((tab) => (tab.amount = stats[tab.id] || 0))
        this.filterByTab()
        this.updatePaginatedItems(this.currentPage)
      })

    this.store.pipe(select(selectNotificationUnreadCount), untilDestroy(this)).subscribe((count) => {
      this.unreadCount = count
    })
  }

  ngOnDestroy(): void {
    super.ngOnDestroy()
  }

  close() {
    this.dialogRef.close()
  }

  getIncidentSeverityColor(severity: IncidentSeverity) {
    switch (severity) {
      case IncidentSeverity.Critical:
        return 'dark-danger'
      case IncidentSeverity.High:
        return 'danger'
      case IncidentSeverity.Medium:
        return 'warning'
      case IncidentSeverity.Low:
      default:
        return 'success'
    }
  }

  getIncidentSeverityIcon(severity: IncidentSeverity) {
    switch (severity) {
      case IncidentSeverity.Critical:
        return 'cs-icon cs-icon-exclamation text-danger'
      case IncidentSeverity.High:
        return 'cs-icon cs-icon-alert-triangle2 text-danger'
      case IncidentSeverity.Medium:
        return 'cs-icon cs-icon-alert-circle text-warning'
      case IncidentSeverity.Low:
      default:
        return 'cs-icon cs-icon-check-circle text-success'
    }
  }

  getTaskStatusColor(task: TaskView) {
    return getTaskStatusColor(task.status) || 'light'
  }

  onReadAll() {
    this.notificationService.readAll().subscribe(
      () => {
        this.unreadCount = 0
        this.store.dispatch(ReadAllNotificationsComplete())
      },
      (error) => {
        this.store.dispatch(ActionFailed({ error }))
      }
    )
  }

  onOpen(notification: Notification) {
    this.notificationDetailService.openDetail(notification)
  }

  onOpenIncident(incident: IncidentView) {
    this.store.dispatch(OpenEditIncidentDialog({ incidentId: incident.pkey }))
  }

  onOpenTask(task: TaskView) {
    this.store.dispatch(OpenEditTaskDialog({ taskId: task.pkey }))
  }

  onTabClick(tab) {
    this.activeTab = tab
    this.filterByTab()
    this.currentPage = 0 // Reset to first page when tab changes
    this.updatePaginatedItems(this.currentPage)
  }

  filterByTab() {
    this.notifications = this.allNotifications.filter((i) => i.type === this.activeTab.id)
  }

  updatePaginatedItems(pageIndex: number) {
    this.paginatedNotifications = this.pageItems(this.notifications, pageIndex)
    this.paginatedIncidents = this.pageItems(this.incidents, pageIndex)
    this.paginatedTasks = this.pageItems(this.tasks, pageIndex)
  }

  onPageChange(event: any) {
    this.currentPage = event.pageIndex
    this.pageSize = event.pageSize
    this.updatePaginatedItems(this.currentPage)
  }

  pageItems(items: any[], pageIndex: number) {
    const startIndex = pageIndex * this.pageSize
    const endIndex = startIndex + this.pageSize
    return items.slice(startIndex, endIndex)
  }

  getTitleHtml(title: string, color: string) {
    return title.replace(/^\s*\w+/, `<span class="text-semi-bold text-${color}">$&</span>`)
  }
}
